import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Zap } from "lucide-react";

export const QuickCampaignDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [entites, setEntites] = useState([]);
  const [form, setForm] = useState({ societe: "", service: "", zone: "", cible: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/entites").then(({ data }) => setEntites(data)).catch(() => {});
  }, [open]);

  const create = async () => {
    if (!form.societe.trim() || !form.service.trim()) {
      toast.error("Indiquez au minimum la société et le service à vendre");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns/quick-create", form);
      toast.success("Brouillon de campagne généré — vérifiez et validez chaque étape");
      onClose();
      navigate(`/campagnes/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Zap size={18} className="text-accent" /> Création rapide par IA — tous métiers B2B</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Fonctionne pour n'importe quel secteur : nettoyage, traiteur, conciergerie, sécurité, avocat, SaaS, transport, formation…
          L'IA construit le ciblage complet (secteurs, codes NAF, décideurs, signaux), que vous validez ensuite.
        </p>
        <div className="space-y-3">
          <div>
            <Label>Société qui prospecte *</Label>
            <Input className="mt-1" list="entites-list" value={form.societe} onChange={(e) => setForm({ ...form, societe: e.target.value })} placeholder="Choisir au catalogue ou saisir librement" data-testid="quick-company-input" />
            <datalist id="entites-list">
              {entites.map((e) => <option key={e.id} value={e.nom} />)}
            </datalist>
          </div>
          <div>
            <Label>Service ou produit à vendre *</Label>
            <Input className="mt-1" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Ex : recherche de mandats de gestion locative" data-testid="quick-service-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Zone géographique</Label>
              <Input className="mt-1" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Ex : Lyon et Rhône (69)" data-testid="quick-zone-input" />
            </div>
            <div>
              <Label>Cible (facultatif)</Label>
              <Input className="mt-1" value={form.cible} onChange={(e) => setForm({ ...form, cible: e.target.value })} placeholder="Ex : propriétaires bailleurs" data-testid="quick-target-input" />
            </div>
          </div>
          <Button onClick={create} disabled={busy} className="w-full bg-accent hover:bg-accent/90" data-testid="quick-create-button">
            {busy ? "Génération IA de la campagne…" : "Générer le brouillon de campagne"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
